import {
    Article,
    AttachMoney,
    Block,
    Favorite,
    InfoOutlined,
    Message,
    MoneyOff,
    Subscriptions,
    TrendingDown,
    TrendingFlat,
    TrendingUp,
    Videocam,
} from '@mui/icons-material';
import { alpha, Box, Card, CardContent, Chip, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React from 'react';
import { Element } from 'react-scroll';
import { OnlyFansUser, SubscribedOnData } from '../../../hooks/useInsightsUserProfile';
import {
    CompareFanSpendingResult,
    CompareFanSpendingResultCategoryConfidence,
    OverallSpendingPattern,
    SimpleFanSpendingGaugeResult,
    SpendingGaugeLevel,
} from '../../../hooks/useInsightsUserSpendingsCompare';

interface SpendingGaugeProps {
    userProfile?: OnlyFansUser | null;
    comparisonResult?: CompareFanSpendingResult | SimpleFanSpendingGaugeResult | null;
    loading: boolean;
    variant?: 'simple' | 'detailed';
}

// Category configuration with icons and labels
const CATEGORY_CONFIG: {
    [key: string]: { label: string; icon: React.ElementType; description: string };
} = {
    totalSumm: {
        label: 'Total Spending',
        icon: AttachMoney,
        description: 'Total amount spent across all categories',
    },
    messagesSumm: {
        label: 'Messages',
        icon: Message,
        description: 'Amount spent on private messages',
    },
    tipsSumm: {
        label: 'Tips',
        icon: Favorite,
        description: 'Amount spent on tips',
    },
    subscribesSumm: {
        label: 'Subscriptions',
        icon: Subscriptions,
        description: 'Amount spent on subscriptions',
    },
    postsSumm: {
        label: 'Posts',
        icon: Article,
        description: 'Amount spent on paid posts',
    },
    streamsSumm: {
        label: 'Streams',
        icon: Videocam,
        description: 'Amount spent on live streams',
    },
};

// Gauge level configuration using theme-based colors
const getGaugeLevelConfig = (theme: any) => ({
    [SpendingGaugeLevel.WELL_BELOW]: {
        label: 'Well Below',
        color: theme.palette.error.main,
        icon: TrendingDown,
        position: 10,
    },
    [SpendingGaugeLevel.BELOW]: {
        label: 'Below',
        color: theme.palette.warning.main,
        icon: TrendingDown,
        position: 30,
    },
    [SpendingGaugeLevel.SIMILAR]: {
        label: 'Similar',
        color: theme.palette.success.main,
        icon: TrendingFlat,
        position: 50,
    },
    [SpendingGaugeLevel.ABOVE]: {
        label: 'Above',
        color: theme.palette.info.main,
        icon: TrendingUp,
        position: 70,
    },
    [SpendingGaugeLevel.WELL_ABOVE]: {
        label: 'Well Above',
        color: theme.palette.primary.main,
        icon: TrendingUp,
        position: 90,
    },
    [SpendingGaugeLevel.NO_SPENDING]: {
        label: 'No Spending',
        color: theme.palette.text.disabled,
        icon: TrendingFlat,
        position: 0,
    },
    [SpendingGaugeLevel.ONLY_SPENDER]: {
        label: 'Only Spender',
        color: theme.palette.secondary.main,
        icon: TrendingUp,
        position: 100,
    },
    [SpendingGaugeLevel.NO_COMPARISON_DATA]: {
        label: 'No Data',
        color: theme.palette.text.secondary,
        icon: InfoOutlined,
        position: 50,
    },
    [SpendingGaugeLevel.ONLY_DATA_POINT]: {
        label: 'Only Data',
        color: theme.palette.text.secondary,
        icon: InfoOutlined,
        position: 50,
    },
});

// Overall pattern configuration using theme colors
const getPatternConfig = (theme: any) => ({
    [OverallSpendingPattern.HIGH_SPENDER]: {
        label: 'High Spender',
        color: theme.palette.primary.main,
        description: 'Consistently spends more across most categories',
    },
    [OverallSpendingPattern.LOW_SPENDER]: {
        label: 'Low Spender',
        color: theme.palette.error.main,
        description: 'Consistently spends less across most categories',
    },
    [OverallSpendingPattern.AVERAGE_SPENDER]: {
        label: 'Average Spender',
        color: theme.palette.success.main,
        description: 'Spends similarly compared to others',
    },
    [OverallSpendingPattern.MIXED_SPENDER]: {
        label: 'Mixed Spender',
        color: theme.palette.warning.main,
        description: 'Varied spending patterns across categories',
    },
    [OverallSpendingPattern.NO_PATTERN]: {
        label: 'No Pattern',
        color: theme.palette.text.secondary,
        description: 'Insufficient data to determine pattern',
    },
});

// Format currency
const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) {
        return '$0';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

// Horizontal gauge component
const HorizontalGauge: React.FC<{
    category: string;
    amount: number;
    gaugeLevel: SpendingGaugeLevel;
    confidence?: CompareFanSpendingResultCategoryConfidence;
    percentile?: number;
    message?: string;
}> = ({ category, amount, gaugeLevel, confidence, percentile, message }) => {
    const theme = useTheme();
    const config = CATEGORY_CONFIG[category];
    const gaugeLevelConfig = getGaugeLevelConfig(theme);
    const gaugeConfig = gaugeLevelConfig[gaugeLevel];
    const IconComponent = config?.icon || AttachMoney;

    const position = percentile !== undefined ? percentile : gaugeConfig.position;

    return (
        <Stack direction="column" spacing={1}>
            <Box sx={{ pb: 2, '&:last-child': { pb: 0 } }}>
                {/* Header row */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Box
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            p: 1,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <IconComponent sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                    </Box>

                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {config?.label || category}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="primary" noWrap>
                            {formatCurrency(amount)}
                        </Typography>
                    </Stack>

                    <Stack alignItems="flex-end" spacing={0.5}>
                        <Chip
                            label={gaugeConfig.label}
                            size="small"
                            sx={{
                                backgroundColor: gaugeConfig.color,
                                color: theme.palette.getContrastText(gaugeConfig.color),
                                // fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                            }}
                        />
                        {percentile !== undefined && (
                            <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                {percentile}th percentile
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                {/* Gauge */}
                <Box sx={{ pb: 2 }}>
                    {/* Labels */}
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            Below
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            Average
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            Above
                        </Typography>
                    </Stack>

                    {/* Track container */}
                    <Box sx={{ position: 'relative', height: 16, mb: 1 }}>
                        {/* Background track */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 6,
                                backgroundColor: theme.palette.grey[200],
                                borderRadius: 3,
                                transform: 'translateY(-50%)',
                            }}
                        />

                        {/* Gradient fill */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 6,
                                background: `linear-gradient(to right, 
                                    ${theme.palette.error.light} 0%, 
                                    ${theme.palette.warning.light} 30%, 
                                    ${theme.palette.success.main} 50%, 
                                    ${theme.palette.info.light} 70%, 
                                    ${theme.palette.primary.main} 100%)`,
                                borderRadius: 3,
                                transform: 'translateY(-50%)',
                            }}
                        />

                        {/* Center marker */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 1.5,
                                height: 12,
                                backgroundColor: theme.palette.text.primary,
                                transform: 'translate(-50%, -50%)',
                                borderRadius: 0.75,
                            }}
                        />

                        {/* Position indicator */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: `${Math.max(6, Math.min(94, position))}%`,
                                width: 16,
                                height: 16,
                                backgroundColor: gaugeConfig.color,
                                border: `2px solid ${theme.palette.background.paper}`,
                                borderRadius: '50%',
                                transform: 'translate(-50%, -50%)',
                                boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.3)}`,
                                zIndex: 2,
                            }}
                        />
                    </Box>

                    {/* Bottom info */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            {confidence && confidence !== CompareFanSpendingResultCategoryConfidence.NONE && (
                                <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                    {`Confidence: ${confidence}`}
                                </Typography>
                            )}
                        </Box>

                        {message && (
                            <Tooltip title={message} arrow placement="top">
                                <InfoOutlined
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        cursor: 'help',
                                        fontSize: 16,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Stack>
    );
};

// Behavioral Metrics Component
const BehavioralMetrics: React.FC<{
    metrics: Array<{
        flag: string;
        confidence: string;
        message: string;
        severity: 'low' | 'medium' | 'high';
    }>;
}> = ({ metrics }) => {
    const theme = useTheme();

    if (!metrics || metrics.length === 0) {
        return null;
    }

    const getMetricConfig = (flag: string) => {
        switch (flag) {
            case 'known_freeloader':
                return {
                    label: 'Known Freeloader',
                    icon: MoneyOff,
                    color: theme.palette.warning.main,
                    bgColor: alpha(theme.palette.warning.main, 0.1),
                };
            case 'often_blocked':
                return {
                    label: 'Often Blocked',
                    icon: Block,
                    color: theme.palette.error.main,
                    bgColor: alpha(theme.palette.error.main, 0.1),
                };
            default:
                return {
                    label: flag,
                    icon: InfoOutlined,
                    color: theme.palette.text.secondary,
                    bgColor: alpha(theme.palette.text.secondary, 0.1),
                };
        }
    };

    const getConfidenceColor = (confidence: string) => {
        switch (confidence) {
            case 'high':
                return theme.palette.error.main;
            case 'medium':
                return theme.palette.warning.main;
            case 'low':
                return theme.palette.info.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    const getSeverityValue = (severity: 'low' | 'medium' | 'high'): number => {
        switch (severity) {
            case 'high':
                return 85;
            case 'medium':
                return 55;
            case 'low':
                return 25;
            default:
                return 0;
        }
    };

    const getSeverityLabel = (severity: 'low' | 'medium' | 'high'): string => {
        switch (severity) {
            case 'high':
                return 'High Risk';
            case 'medium':
                return 'Moderate Risk';
            case 'low':
                return 'Low Risk';
            default:
                return 'Unknown';
        }
    };

    return (
        <Box>
            <Stack spacing={2}>
                <Typography variant="h6" fontWeight={600}>
                    Behavioral Flags
                </Typography>

                {metrics.map((metric, index) => {
                    const config = getMetricConfig(metric.flag);
                    const IconComponent = config.icon;
                    const severityValue = getSeverityValue(metric.severity);
                    const severityLabel = getSeverityLabel(metric.severity);

                    return (
                        <Element id={`metric-${metric.flag}`} name={`metric-${metric.flag}`} key={index}>
                            <Card
                                sx={{
                                    border: `1px solid ${config.color}`,
                                    backgroundColor: config.bgColor,
                                }}
                            >
                                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack spacing={2}>
                                        {/* Header */}
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1.5,
                                                    backgroundColor: alpha(config.color, 0.15),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <IconComponent sx={{ color: config.color, fontSize: 24 }} />
                                            </Box>

                                            <Stack sx={{ flex: 1 }}>
                                                <Typography variant="h6" fontWeight={600} color={config.color}>
                                                    {config.label}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {metric.message}
                                                </Typography>
                                            </Stack>

                                            <Stack alignItems="flex-end" spacing={0.5}>
                                                <Chip
                                                    label={metric.confidence}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getConfidenceColor(metric.confidence),
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 22,
                                                        textTransform: 'uppercase',
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>

                                        {/* Severity indicator */}
                                        <Box>
                                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                                    Risk Level
                                                </Typography>
                                                <Typography variant="caption" color={config.color} fontSize="0.7rem" fontWeight={600}>
                                                    {severityLabel}
                                                </Typography>
                                            </Stack>

                                            <Box sx={{ position: 'relative', height: 8 }}>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '100%',
                                                        backgroundColor: theme.palette.grey[200],
                                                        borderRadius: 4,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: `${severityValue}%`,
                                                        height: '100%',
                                                        backgroundColor: config.color,
                                                        borderRadius: 4,
                                                        transition: 'width 0.3s ease-in-out',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Element>
                    );
                })}
            </Stack>
        </Box>
    );
};

// Overall pattern summary component
const OverallPatternSummary: React.FC<{
    pattern: OverallSpendingPattern;
    breakdown?: { above: number; similar: number; below: number; total: number };
}> = ({ pattern, breakdown }) => {
    const theme = useTheme();
    const patternConfig = getPatternConfig(theme);
    const config = patternConfig[pattern];

    // Calculate overall position based on breakdown
    let overallPosition = 50; // Default to center

    if (breakdown && breakdown.total > 0) {
        const abovePercentage = (breakdown.above / breakdown.total) * 100;
        const belowPercentage = (breakdown.below / breakdown.total) * 100;
        const similarPercentage = (breakdown.similar / breakdown.total) * 100;

        if (abovePercentage >= 60) {
            overallPosition = 80; // High spender
        } else if (belowPercentage >= 60) {
            overallPosition = 20; // Low spender
        } else if (similarPercentage >= 50) {
            overallPosition = 50; // Average spender
        } else {
            // Mixed spender - position based on weighted average
            overallPosition = abovePercentage * 0.8 + similarPercentage * 0.5 + belowPercentage * 0.2;
        }
    }

    return (
        <Box sx={{ mb: 3 }}>
            {/* Header */}
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                    Overall Spending Pattern
                </Typography>
                <Chip
                    label={config.label}
                    sx={{
                        backgroundColor: config.color,
                        color: theme.palette.getContrastText(config.color),
                        fontWeight: 600,
                        fontSize: '0.875rem',
                    }}
                />
            </Stack>
            <Stack spacing={3}>
                <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        {config.description}
                    </Typography>
                </Stack>

                {/* Compact horizontal gauge */}
                <Box sx={{ px: 0 }}>
                    {/* Labels */}
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            Low Spender
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            Average
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            High Spender
                        </Typography>
                    </Stack>

                    {/* Track container */}
                    <Box sx={{ position: 'relative', height: 20, mb: 1.5 }}>
                        {/* Background track */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 8,
                                backgroundColor: theme.palette.grey[200],
                                borderRadius: 4,
                                transform: 'translateY(-50%)',
                            }}
                        />

                        {/* Gradient fill */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: 8,
                                background: `linear-gradient(to right, 
                                    ${theme.palette.error.light} 0%, 
                                    ${theme.palette.warning.light} 30%, 
                                    ${theme.palette.success.main} 50%, 
                                    ${theme.palette.info.light} 70%, 
                                    ${theme.palette.primary.main} 100%)`,
                                borderRadius: 4,
                                transform: 'translateY(-50%)',
                            }}
                        />

                        {/* Center marker */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 2,
                                height: 16,
                                backgroundColor: theme.palette.text.primary,
                                transform: 'translate(-50%, -50%)',
                                borderRadius: 1,
                            }}
                        />

                        {/* Position indicator */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: `${Math.max(8, Math.min(92, overallPosition))}%`,
                                width: 16,
                                height: 16,
                                backgroundColor: config.color,
                                border: `2px solid ${theme.palette.background.paper}`,
                                borderRadius: '50%',
                                transform: 'translate(-50%, -50%)',
                                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
                                zIndex: 2,
                            }}
                        />
                    </Box>

                    {/* Breakdown chips */}
                    {breakdown && breakdown.total > 0 && (
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                            <Chip
                                size="small"
                                label={`${breakdown.below} below`}
                                sx={{
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    color: theme.palette.error.main,
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`${breakdown.similar} similar`}
                                sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    color: theme.palette.success.main,
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`${breakdown.above} above`}
                                sx={{
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                            />
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

// Main component
const FanSpendingGauge: React.FC<SpendingGaugeProps> = ({ userProfile, comparisonResult, loading, variant = 'detailed' }) => {
    const theme = useTheme();

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (!userProfile || !comparisonResult) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                    No data available
                </Typography>
            </Box>
        );
    }

    const categories: string[] = Object.keys(CATEGORY_CONFIG);
    const isDetailed = variant === 'detailed' && 'categories' in comparisonResult && comparisonResult.categories;

    return (
        <Box>
            <Stack spacing={2}>
                {/* Category Gauges */}
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        How much this fan spends in each category on your page compared to other creator pages. Each gauge shows their
                        spending level, confidence, and percentile rank.
                    </Typography>

                    {/* Overall Pattern Summary */}
                    {isDetailed && comparisonResult instanceof Object && 'summary' in comparisonResult && comparisonResult.summary && (
                        <OverallPatternSummary
                            pattern={comparisonResult.summary.overallPattern.pattern}
                            breakdown={comparisonResult.summary.overallPattern.breakdown}
                        />
                    )}

                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        Spending Categories
                    </Typography>

                    {categories.map((category: string) => {
                        const amount = (userProfile.subscribedOnData[category as keyof SubscribedOnData] as number) || 0;
                        let gaugeLevel = SpendingGaugeLevel.SIMILAR;
                        let confidence, percentile, message;

                        if (isDetailed && comparisonResult.categories && comparisonResult.categories[category]) {
                            const categoryData = comparisonResult.categories[category];

                            if (categoryData instanceof Object && 'gauge' in categoryData) {
                                gaugeLevel = categoryData.gauge;
                                confidence = categoryData.confidence;
                                percentile = categoryData.percentile;
                                message = categoryData.message;
                            }
                        } else if ('categories' in comparisonResult && comparisonResult.categories) {
                            // Simple result
                            const simpleLevel = comparisonResult.categories[category];
                            if (simpleLevel === 'above') gaugeLevel = SpendingGaugeLevel.ABOVE;
                            else if (simpleLevel === 'below') gaugeLevel = SpendingGaugeLevel.BELOW;
                            else if (simpleLevel === 'similar') gaugeLevel = SpendingGaugeLevel.SIMILAR;
                        }

                        return (
                            <HorizontalGauge
                                key={category}
                                category={category}
                                amount={amount}
                                gaugeLevel={gaugeLevel}
                                confidence={confidence}
                                percentile={percentile}
                                message={message}
                            />
                        );
                    })}
                </Box>

                {isDetailed && comparisonResult instanceof Object && 'summary' in comparisonResult && comparisonResult.summary && (
                    <>
                        {/* Behavioral Metrics */}
                        {comparisonResult.summary.behavioralMetrics && comparisonResult.summary.behavioralMetrics.length > 0 && (
                            <BehavioralMetrics metrics={comparisonResult.summary.behavioralMetrics} />
                        )}
                    </>
                )}

                {/* Status Message */}
                {comparisonResult.message && (
                    <Paper
                        sx={{
                            p: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {comparisonResult.message}
                        </Typography>
                    </Paper>
                )}
            </Stack>
        </Box>
    );
};

export default FanSpendingGauge;
